/**
 * Utility functions for encoding and decoding filters in URL parameters
 * Uses base64 encoding with simple obfuscation for URL storage
 */

/**
 * Encode filters into a base64 string for URL storage
 * @param {Array} filterGroups - Array of filter groups from ChipsContactFilter
 * @returns {string} - Base64 encoded string containing the filters
 */
export const encodeFiltersToUrl = (filterGroups = []) => {
  try {
    if (!filterGroups || filterGroups.length === 0) {
      return "";
    }

    // Create payload with filters and timestamp
    const payload = {
      filters: filterGroups,
      timestamp: Date.now(),
    };

    // Convert to JSON string and encode with base64
    const jsonString = JSON.stringify(payload);
    const encoded = btoa(jsonString);
    return encoded;
  } catch (error) {
    console.error("Error encoding filters to URL:", error);
    return "";
  }
};

/**
 * Decode filters from a base64 string in URL
 * @param {string} encodedString - Base64 encoded string from URL parameter
 * @returns {Array} - Array of filter groups, or empty array if invalid
 */
export const decodeFiltersFromUrl = (encodedString) => {
  try {
    if (!encodedString || encodedString.trim() === "") {
      return [];
    }

    // Decode base64 string
    const jsonString = atob(encodedString);
    const decoded = JSON.parse(jsonString);

    // Validate payload structure
    if (!decoded.filters || !Array.isArray(decoded.filters)) {
      console.warn("Invalid filter structure in URL");
      return [];
    }

    // Optional: Check if the data is too old (e.g., older than 7 days)
    const maxAge = 7 * 24 * 60 * 60 * 1000; // 7 days in milliseconds
    if (decoded.timestamp && Date.now() - decoded.timestamp > maxAge) {
      console.warn("Filter data is too old, ignoring");
      return [];
    }

    return decoded.filters;
  } catch (error) {
    console.error("Error decoding filters from URL:", error);
    return [];
  }
};

/**
 * Update URL with encoded filters
 * @param {Array} filterGroups - Array of filter groups
 * @param {Function} navigate - Navigation function (from useNavigate or similar)
 */
export const updateUrlWithFilters = (filterGroups, navigate) => {
  try {
    const encodedFilters = encodeFiltersToUrl(filterGroups);

    // Create new URL with filters parameter
    const url = new URL(window.location);

    if (encodedFilters) {
      url.searchParams.set("filters", encodedFilters);
    } else {
      url.searchParams.delete("filters");
    }

    // Update URL without triggering a page reload
    navigate(url.pathname + url.search, { replace: true });
  } catch (error) {
    console.error("Error updating URL with filters:", error);
  }
};

/**
 * Get filters from current URL
 * @returns {Array} - Array of filter groups from URL, or empty array if none
 */
export const getFiltersFromUrl = () => {
  try {
    const url = new URL(window.location);
    const filtersParam = url.searchParams.get("filters");

    if (!filtersParam) {
      return [];
    }

    return decodeFiltersFromUrl(filtersParam);
  } catch (error) {
    console.error("Error getting filters from URL:", error);
    return [];
  }
};

/**
 * Clear filters from URL
 * @param {Function} navigate - Navigation function
 */
export const clearFiltersFromUrl = (navigate) => {
  try {
    const url = new URL(window.location);
    url.searchParams.delete("filters");
    navigate(url.pathname + url.search, { replace: true });
  } catch (error) {
    console.error("Error clearing filters from URL:", error);
  }
};
